import {
  Button,
  Group,
  Input,
  Modal,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure, useUncontrolled } from "@mantine/hooks"
import {
  IconCloudSearch,
  IconExclamationCircle,
  IconPencil,
} from "@tabler/icons-react"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"

import {
  Allocation,
  VirtualDimensionsRequests,
} from "@costory/types/endpoints/virtualDimensions"
import { Prisma } from "@costory/types/prisma-client"

import { formatNumber } from "@costory/shared/utils/format"

import { SelectCreatable } from "@costory/front/components/SelectCreatable"
import { getDefaultAllocationConfig } from "@costory/front/pages/VirtualDimensions/components/RuleEditor"
import SplitCostResult from "@costory/front/pages/VirtualDimensions/components/SplitCostResult"
import { ALLOCATION_TYPES } from "@costory/front/pages/VirtualDimensions/constants"
import { showError } from "@costory/front/utils/validation"

type Props = {
  form: UseFormReturnType<VirtualDimensionsRequests.EditVirtualDimension>
  allocatedCost: number | undefined
  exploreCost: () => void
  organizationUsers?: (Prisma.UserGetPayload<Record<string, never>> & {
    fullName: string
  })[]
}

export const Leftovers = ({
  form,
  allocatedCost,
  exploreCost,
  organizationUsers = [],
}: Props) => {
  const [isOpen, { open, close }] = useDisclosure()
  const [allocationConfigs, setAllocationConfigs] = useState<
    Record<string, Allocation>
  >({})

  const hasError = useMemo(() => {
    const formErrorsKeys = Object.keys(form.errors)
    return formErrorsKeys.some((key) => key.includes("leftoverRule"))
  }, [form.errors])
  const allocation = form.getValues().leftoverRule.allocation

  const {
    value: dimentionValue,
    defaultValue,
    onChange,
  } = form.getInputProps("leftoverRule.allocation.dimensionValue")

  const [value, setValue] = useUncontrolled({
    value: dimentionValue,
    defaultValue,
    finalValue: "Other",
    onChange,
  })
  useEffect(() => {
    const defaultConfig = getDefaultAllocationConfig(allocation.allocationType)
    if (!_.isEqual(allocation, defaultConfig)) {
      setAllocationConfigs({})
    }
  }, [allocation])

  const ownerSelectItems = useMemo(() => {
    if (!organizationUsers || organizationUsers.length === 0) {
      return []
    }
    return organizationUsers
      .filter((user) => user?.id && user?.fullName)
      .map((user) => ({
        value: String(user.id),
        label: user.fullName,
      }))
  }, [organizationUsers])

  return (
    <>
      <Paper data-intercom-target="vdim-leftovers-paper">
        <Stack p={16}>
          <Group justify="space-between">
            <Group gap={4}>
              {hasError && (
                <Tooltip
                  label={
                    <>
                      {showError({
                        prefix: "leftover",
                        errors: form.errors,
                      }).join(",")}
                    </>
                  }
                >
                  <ThemeIcon c="red.5">
                    <IconExclamationCircle />
                  </ThemeIcon>
                </Tooltip>
              )}

              <Text size="lg" fw="bold" c={hasError ? "red.5" : "inherit"}>
                {form.getValues().leftoverRule.name}
              </Text>
            </Group>
            <Group gap="md" align="center">
              {allocatedCost ? (
                <Text>{formatNumber({ value: allocatedCost })}</Text>
              ) : null}
              <Select
                placeholder="Select owner"
                data={ownerSelectItems}
                value={form.getValues().leftoverRule.ownerId || null}
                onChange={(value) => {
                  form.setFieldValue("leftoverRule.ownerId", value || undefined)
                }}
                error={
                  form.getInputProps("leftoverRule.ownerId").error !== undefined
                }
                w={260}
                clearable
              />
              <Button
                size="xs"
                color="textMuted"
                variant="outline"
                leftSection={<IconPencil size={16} />}
                onClick={open}
                data-intercom-target="costory-tour-vdim-rename-leftover"
              >
                Rename rule
              </Button>
            </Group>
          </Group>

          <Group justify="space-between">
            <Group>
              <SegmentedControl
                data={ALLOCATION_TYPES.filter(
                  (a) =>
                    a.value !== "existingColumn" && a.value !== "telemetry",
                )}
                key={form.key("leftoverRule.allocation.allocationType")}
                {...form.getInputProps(
                  "leftoverRule.allocation.allocationType",
                )}
                onChange={(val) => {
                  const currentType = allocation.allocationType
                  if (currentType && currentType !== val) {
                    setAllocationConfigs((prev) => ({
                      ...prev,
                      [currentType]: { ...allocation },
                    }))
                  }
                  const restoredConfig =
                    allocationConfigs[val] || getDefaultAllocationConfig(val)
                  form.setFieldValue("leftoverRule.allocation", restoredConfig)
                  form.validate()
                }}
                data-intercom-target="costory-tour-vdim-leftover-allocation-type"
              />
              {form.getValues().leftoverRule.allocation.allocationType ==
                "dimensionValue" && (
                <SelectCreatable
                  key={form.key("leftoverRule.allocation.dimensionValue")}
                  data={form.getValues().values}
                  {...form.getInputProps(
                    "leftoverRule.allocation.dimensionValue",
                  )}
                  handleCreate={(val) => form.insertListItem("values", val)}
                  value={value}
                  onChange={setValue}
                  error={
                    form.getInputProps("leftoverRule.allocation.dimensionValue")
                      .error !== undefined
                  }
                  data-intercom-target="costory-tour-vdim-leftover-dimension-value"
                />
              )}
            </Group>
            <Button
              variant="outline"
              size="sm"
              leftSection={<IconCloudSearch />}
              onClick={exploreCost}
              data-intercom-target="costory-tour-vdim-explore-leftover-costs"
            >
              Explore leftovers costs
            </Button>
          </Group>
          <SplitCostResult
            allocation={allocation}
            prefixForm="leftoverRule.allocation"
            form={form}
            costRule={allocatedCost || 0}
            vdDict={_.uniq(form.getValues().values)}
          />
        </Stack>
        <Modal
          opened={isOpen}
          onClose={close}
          title="Rename a rule"
          withinPortal
        >
          <Group>
            <Input
              {...form.getInputProps("leftoverRule.name")}
              flex={1}
              onKeyDown={(key) => (key.key == "Enter" ? close() : null)}
              data-intercom-target="costory-tour-vdim-leftover-name-input"
            />
            <Button
              onClick={close}
              data-intercom-target="costory-tour-vdim-save-leftover-name"
            >
              Save
            </Button>
          </Group>
        </Modal>
      </Paper>
    </>
  )
}
